import * as rrweb from 'rrweb';
import * as pako from 'pako';
import {URI} from '@/rest/api';
import {getAuthHeader} from '@/SessionReplay/sessionReplayApi';
import {createConsoleRecordPlugin} from '@/SessionReplay/consoleRecordPlugin';
import {resetAccumulator, buildAccumulatedSummary} from '@/SessionReplay/sessionSummaryAccumulator';
// OWA-675: the masking policy lives in its own dependency-free module so forms
// can unit-test it against their rendered DOM without importing this one.
import {shouldMaskInput} from '@/lib/sessionReplayMasking';

export function isRecordingActive(): boolean {
	return sessionId !== null;
}

// Session storage keys for resuming across page reloads (per-TAB by design —
// each tab records its own rrweb stream; see LS_VISIT_ID below for the
// cross-tab grouping key).
const SS_SESSION_ID = 'replay_sessionId';
const SS_CHUNK_INDEX = 'replay_chunkIndex';

// Local storage keys for the visit — shared across every tab on this origin.
const LS_VISIT_ID = 'replay_visitId';
const LS_VISIT_LAST_ACTIVITY = 'replay_visitLastActivity';

// Singleton state
let sessionId: string | null = null;
let stopFn: (() => void) | null = null;
let flushInterval: ReturnType<typeof setInterval> | null = null;
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let eventBuffer: object[] = [];
let chunkIndex = 0;
let flushing = false;
let recordingPaused = false;

/** Credentials from the first startRecording() call, kept so the session can be
 *  restarted without a page reload (the app only calls startRecording() once,
 *  from setLoggingUser() in remoteLogging.ts). */
let recordingUserId: string | null = null;
let recordingFirmId: string | null = null;

/** Timestamp (ms) of the last successful server contact for this session —
 *  a chunk upload or a heartbeat. Drives the heartbeat cadence below. */
let lastServerContact = 0;

/** True while a one-shot activity listener is armed to restart recording after
 *  an inactivity close. Not armed on logout — see stopRecording(). */
let restartArmed = false;
let restartListener: (() => void) | null = null;

/** True while stopRecording() is mid-flight. Its final flush can still get a
 *  403/404 from the server; reacting to those with another stop or a restart
 *  would re-enter teardown or open a new session during logout. */
let stopping = false;

/** True while startRecording() is mid-flight. `sessionId` cannot serve as the
 *  re-entry guard on its own: it is not assigned until after the awaited
 *  resume-check and session POST, so every caller arriving inside that window
 *  passes the `if (sessionId)` check and starts its own rrweb recorder. Only the
 *  last one is reachable through the module-level stopFn, leaving the others'
 *  whole-document MutationObserver, console wrapper and flush interval live for
 *  the lifetime of the tab. Set synchronously before the first await. */
let starting = false;

/** Bumped by every stopRecording(). A start that was still awaiting the server
 *  when the stop ran (logout in particular) has no session id for the stop to
 *  tear down, so it compares this before attaching and abandons itself. */
let recordingEpoch = 0;

/** True once the page has begun unloading. A 404 on the unload flush must not
 *  open a replacement session in a page that is going away. Self-clears in
 *  case the unload is canceled (the timer never runs if the page truly dies). */
let unloading = false;

/** Releases this tab's exclusive claim on its session id. Duplicating a tab
 *  copies sessionStorage, so two live tabs would otherwise resume the SAME
 *  session and overwrite each other's chunks (the server upserts on
 *  (session_id, chunk_index)). Held for as long as we record. */
let releaseSessionClaim: (() => void) | null = null;

/** Settles once the lock manager has actually handed the claim back. Releasing
 *  is asynchronous, so re-claiming without awaiting this would race. */
let sessionClaimDone: Promise<void> | null = null;

/** Drop this tab's claim and wait for the lock manager to confirm. */
async function releaseSessionClaimAndWait(): Promise<void> {
	if (!releaseSessionClaim) return;
	releaseSessionClaim();
	try {
		await sessionClaimDone;
	}
	catch {
		// The request promise rejecting still means we no longer hold it.
	}
	sessionClaimDone = null;
}

/**
 * Claim exclusive ownership of a session id for this tab.
 *
 * Returns false only when another live tab already holds the claim — the
 * caller must then abandon that session id and create a fresh one. Where Web
 * Locks is unavailable, returns true: we're no worse off than before.
 */
async function claimSession(id: string): Promise<boolean> {
	await releaseSessionClaimAndWait();

	if (typeof navigator === 'undefined' || !navigator.locks) return true;

	return new Promise<boolean>(resolve => {
		// The lock is held until we call release, so the callback parks on a
		// promise we resolve at teardown. `ifAvailable` makes this a test rather
		// than a wait: another tab holding it means a duplicated tab, and we
		// want to know now, not whenever that tab happens to close.
		sessionClaimDone = navigator.locks.request(`replay-session-${id}`, {ifAvailable: true}, lock => {
			if (!lock) {
				resolve(false);
				return Promise.resolve();
			}
			return new Promise<void>(release => {
				releaseSessionClaim = () => {
					releaseSessionClaim = null;
					release();
				};
				resolve(true);
			});
		}).then(() => undefined).catch(() => {
			// Lock manager unavailable — never block recording over it.
			resolve(true);
		});
	});
}

/**
 * Record which tab the user is actually looking at, as rrweb custom events.
 *
 * Playback needs this to follow the active tab across a multi-tab visit
 * (OWA-669). Deriving it later from event density would be guesswork, and the
 * data can only be captured while recording — so capture it now, even though
 * the player that consumes it ships later.
 */
function emitWindowFocusState(): void {
	try {
		rrweb.record.addCustomEvent(document.hasFocus() ? 'windowFocus' : 'windowBlur', {});
	}
	catch {
		// addCustomEvent throws if the recorder isn't attached; nothing to record.
	}
}

function handleWindowFocus(): void {
	touchVisitActivity();
	emitWindowFocusState();
}

/** Routes where we must not record — their DOMs are session-replay viewers
 *  (or related tooling) whose huge serialized trees break the recorder. */
const NO_RECORD_PATH_PREFIXES = ['/session-replay'];

function isNoRecordRoute(): boolean {
	return NO_RECORD_PATH_PREFIXES.some(p => window.location.pathname.startsWith(p));
}

function persistSessionState(): void {
	if (sessionId) {
		sessionStorage.setItem(SS_SESSION_ID, sessionId);
		sessionStorage.setItem(SS_CHUNK_INDEX, String(chunkIndex));
	}
}

function clearPersistedSession(): void {
	sessionStorage.removeItem(SS_SESSION_ID);
	sessionStorage.removeItem(SS_CHUNK_INDEX);
}

/** How long a visit survives with no activity in ANY tab before the next
 *  recording starts a new one. Matches INACTIVITY_TIMEOUT so a visit and a
 *  session agree on where one sitting ends and the next begins. */
const VISIT_IDLE_TIMEOUT = 30 * 60 * 1000;

/** Don't write to localStorage on every scroll tick. */
const VISIT_ACTIVITY_WRITE_THROTTLE_MS = 60 * 1000;

/** The visit this tab's session belongs to. Sent when creating a session so
 *  the list can group a multi-tab sitting into one row (OWA-664). */
let visitId: string | null = null;
let lastVisitActivityWrite = 0;

function newUuid(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	// Older WebKit only exposes randomUUID on secure origins. A visit id is a
	// grouping key, never a security token, so a random fallback is fine.
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Join the visit already in progress on this origin, or start a new one.
 *
 * localStorage is shared by every tab on the origin, so a second tab opened
 * while the user is working joins the first tab's visit instead of looking
 * like a separate sitting. Each tab still records its OWN session and its own
 * rrweb stream — only the grouping is shared.
 */
function acquireVisitId(): {id: string; isNew: boolean} {
	try {
		const existing = localStorage.getItem(LS_VISIT_ID);
		const lastActivity = parseInt(localStorage.getItem(LS_VISIT_LAST_ACTIVITY) || '', 10);
		if (existing && Number.isFinite(lastActivity) && Date.now() - lastActivity < VISIT_IDLE_TIMEOUT) {
			touchVisitActivity(true);
			return {id: existing, isNew: false};
		}
		const fresh = newUuid();
		localStorage.setItem(LS_VISIT_ID, fresh);
		touchVisitActivity(true);
		return {id: fresh, isNew: true};
	}
	catch {
		// localStorage can throw (Safari private browsing, storage disabled).
		// Fall back to a tab-local visit: grouping degrades to one-row-per-tab,
		// which is exactly today's behaviour, and nothing else breaks.
		return {id: newUuid(), isNew: true};
	}
}

/** Push the visit's activity clock forward. Throttled unless `force`. */
function touchVisitActivity(force = false): void {
	const now = Date.now();
	if (!force && now - lastVisitActivityWrite < VISIT_ACTIVITY_WRITE_THROTTLE_MS) return;
	lastVisitActivityWrite = now;
	try {
		localStorage.setItem(LS_VISIT_LAST_ACTIVITY, String(now));
	}
	catch {
		// Non-fatal; see acquireVisitId().
	}
}

/** Maximum retry attempts for a failed chunk upload */
const MAX_RETRIES = 2;

/** Initial backoff delay between retry attempts (ms). Doubles each retry. */
const RETRY_BACKOFF_BASE_MS = 250;

/** Close the session after 30 minutes of no user interaction. This is the
 *  industry-standard session window (LogRocket uses the same). The server's
 *  lazy-close sweep uses a deliberately longer window (60 minutes) so the
 *  client always ends a session first and the server only catches tabs that
 *  crashed or vanished. Do NOT raise this above the server's window. */
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

/** Send a heartbeat when the session has had no server contact for this long.
 *  Chunk uploads count as contact, so this only fires on a quiet session.
 *  Must stay well under the server's 60-minute lazy-close window. */
const HEARTBEAT_IDLE_MS = 4 * 60 * 1000;

/** Maximum events to buffer before dropping oldest non-FullSnapshot events */
const MAX_BUFFER_SIZE = 10_000;

/** Maximum time (ms) to wait for an in-flight flush before giving up */
const FLUSH_WAIT_TIMEOUT = 5_000;

/** Scheduled flush cadence. Larger = fewer requests but more delay before
 *  events land on the server. 30s gives ~2 chunks/min on a busy session,
 *  down from 12/min at the previous 5s cadence. */
const FLUSH_INTERVAL_MS = 30_000;

/** Eager-flush threshold. When the buffer reaches this many events between
 *  scheduled flushes, kick a flush immediately so a busy page doesn't grow
 *  one giant chunk (and so the server sees events sooner than 30s later).
 *  200 rrweb events ≈ 50–150KB uncompressed — a comfortable chunk size. */
const EAGER_FLUSH_EVENT_COUNT = 200;

let eagerFlushQueued = false;

/** Queue a one-shot flush on the next tick. Guarded so multiple emit()
 *  calls in a row coalesce into a single scheduled flush. */
function scheduleEagerFlush(): void {
	if (eagerFlushQueued || flushing) return;
	eagerFlushQueued = true;
	setTimeout(() => {
		eagerFlushQueued = false;
		flushBuffer().catch(err => console.warn('Session replay: eager flush failed', err));
	}, 0);
}

/** Keepalive requests share a 64KB body limit per origin (fetch spec).
 *  Use 60KB threshold, matching Sentry's conservative buffer. */
const KEEPALIVE_BODY_LIMIT = 60_000;

/**
 * Flush the current event buffer to the server as a compressed chunk.
 *
 * @param keepalive - If true, use fetch keepalive (for beforeunload/visibilitychange
 *   so the request survives page close). Regular interval flushes should pass false
 *   because the keepalive body limit (64KB across all pending requests) can silently
 *   drop large chunks like the initial FullSnapshot.
 * @param authHeader - Pre-captured auth header. Used during logout when
 *   localStorage may be cleared before the flush completes.
 *
 * On upload failure, events are pushed back into the buffer so they
 * are included in the next flush (preventing silent data loss).
 */
async function flushBuffer(keepalive = false, authHeader?: string): Promise<void> {
	if (!sessionId || eventBuffer.length === 0 || flushing) return;

	flushing = true;
	const events = eventBuffer.splice(0, eventBuffer.length);
	// Snapshot the current index but DON'T advance it yet — if all retries
	// fail, events get re-queued and the next flush should retry under the
	// same chunk number rather than leaving a permanent gap in the chunk
	// sequence on the server.
	const currentIndex = chunkIndex;
	const auth = authHeader || getAuthHeader();

	try {
		const json = JSON.stringify(events);
		const compressed = pako.gzip(json);

		// Only use keepalive if the compressed body fits within the browser limit.
		// Per the fetch spec, keepalive requests share a 64KB body limit per origin.
		const useKeepalive = keepalive && compressed.byteLength <= KEEPALIVE_BODY_LIMIT;

		let uploaded = false;
		for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
			// Exponential backoff before retries (250ms, 500ms). First attempt
			// fires immediately; subsequent ones wait so a transient network
			// blip has time to clear instead of three back-to-back failures.
			if (attempt > 0) {
				await new Promise(r => setTimeout(r, RETRY_BACKOFF_BASE_MS * (1 << (attempt - 1))));
			}
			// After a keepalive failure, fall back to regular fetch for retries
			const ka = attempt === 0 ? useKeepalive : false;
			try {
				const response = await fetch(
					`${URI}/replay/sessions/${sessionId}/chunks?chunkIndex=${currentIndex}&eventCount=${events.length}`,
					{
						method: 'POST',
						headers: {
							'Authentication': auth,
							'Content-Type': 'application/octet-stream',
						},
						body: compressed,
						keepalive: ka,
					}
				);

				if (response.ok) {
					uploaded = true;
					chunkIndex = currentIndex + 1;
					lastServerContact = Date.now();
					persistSessionState();
					break;
				}

				// 403 = replay disabled or the user opted out. Stop for good;
				// restarting would only be refused again. If this flush IS the
				// stop's final flush, the stop already owns teardown — reacting
				// here would re-enter it.
				if (response.status === 403) {
					console.warn('Session replay: replay refused (403), stopping recording');
					flushing = false;
					if (!stopping) stopRecording().catch(() => {});
					return;
				}

				// 404 = the session no longer exists server-side (reaped by the
				// lazy-close sweep, a server restart, ...). Recording must NOT die
				// with it — before this, one idle gap left the rest of the tab's
				// lifetime unrecorded. The buffered events reference the dead
				// session's snapshot chain, so they can't go anywhere; a new
				// session opens with its own FullSnapshot.
				if (response.status === 404) {
					flushing = false;
					eventBuffer = [];
					// Mid-stop: the stop finishes its teardown; nothing to revive.
					// Mid-unload: never open a session in a dying page.
					if (stopping || unloading) return;
					if (document.visibilityState === 'hidden') {
						// No user in front of a hidden tab — wait for real activity
						// instead of opening a ghost session nobody is driving.
						console.warn('Session replay: session gone (404) in hidden tab, will restart on activity');
						teardownLocalRecording();
						armRestartOnActivity();
						return;
					}
					console.warn('Session replay: session gone (404), starting a new one');
					restartSession().catch(err => console.warn('Session replay: restart failed', err));
					return;
				}

				console.warn(`Session replay: chunk ${currentIndex} upload returned ${response.status} (attempt ${attempt + 1})`);
			}
			catch (err) {
				console.warn(`Session replay: chunk ${currentIndex} network error (attempt ${attempt + 1})`, err);
			}
		}

		if (!uploaded) {
			// Prepend events back into the buffer so they're included in the
			// next flush. Uses array concat to avoid call-stack limits from
			// spreading large event arrays into unshift().
			eventBuffer = [...events, ...eventBuffer];
			console.warn(`Session replay: chunk ${currentIndex} failed after ${MAX_RETRIES + 1} attempts, events re-queued`);
		}
	}
	catch (err) {
		// Compression or stringify failure — re-queue events
		eventBuffer = [...events, ...eventBuffer];
		console.warn('Session replay: failed to prepare chunk', err);
	}
	finally {
		flushing = false;
	}
}

/**
 * Tell the server this session is still alive.
 *
 * The server closes any session whose `updated_at` has gone stale, and
 * `updated_at` only advances on chunk upload. A user who is reading, on a
 * call, or sitting on a backgrounded tab generates no events and therefore no
 * uploads, so without this the session gets closed underneath them.
 *
 * A 404 means the session was already closed — recycle into a new one rather
 * than heartbeating a corpse.
 */
async function sendHeartbeat(): Promise<void> {
	if (!sessionId) return;

	const heartbeatSessionId = sessionId;
	try {
		const response = await fetch(`${URI}/replay/sessions/${heartbeatSessionId}/heartbeat`, {
			method: 'POST',
			headers: {'Authentication': getAuthHeader()},
		});

		if (response.ok) {
			lastServerContact = Date.now();
			return;
		}

		if (response.status === 404 && sessionId === heartbeatSessionId) {
			// A heartbeat only fires when the buffer is empty, so the user is
			// quiet by definition. Don't open a replacement session nobody is
			// driving — tear down and let the next real interaction start one.
			console.warn('Session replay: session gone (404 on heartbeat), will restart on activity');
			eventBuffer = [];
			teardownLocalRecording();
			armRestartOnActivity();
			return;
		}

		console.warn(`Session replay: heartbeat returned ${response.status}`);
	}
	catch (err) {
		// Transient network failure — the next interval tick retries.
		console.warn('Session replay: heartbeat failed', err);
	}
}

/**
 * One tick of the scheduled interval.
 *
 * Buffered events? Upload them — that doubles as proof of life. Nothing
 * buffered and the server hasn't heard from us in a while? Heartbeat, so a
 * quiet-but-live session isn't closed underneath the user.
 */
async function flushIntervalTick(): Promise<void> {
	if (eventBuffer.length > 0) {
		await flushBuffer();
		return;
	}
	if (Date.now() - lastServerContact >= HEARTBEAT_IDLE_MS) {
		await sendHeartbeat();
	}
}

/**
 * Tear down this tab's local recording state without touching the server.
 * Used when the server-side session is already gone, so there is nothing to
 * flush or PATCH — see stopRecording() for the clean-shutdown path.
 */
function teardownLocalRecording(): void {
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	if (flushInterval) {
		clearInterval(flushInterval);
		flushInterval = null;
	}
	document.removeEventListener('visibilitychange', handleVisibilityChange);
	window.removeEventListener('beforeunload', handleBeforeUnload);
	window.removeEventListener('focus', handleWindowFocus);
	window.removeEventListener('blur', emitWindowFocusState);
	stopInactivityWatch();

	// Let another tab (or our own next session) take this id.
	releaseSessionClaim?.();

	sessionId = null;
	chunkIndex = 0;
	flushing = false;
	clearPersistedSession();
}

/**
 * Replace a session that no longer exists server-side with a fresh one, in
 * place, without a page reload. The app calls startRecording() exactly once
 * per page load, so without this a single 404 ends recording for the lifetime
 * of the tab.
 */
async function restartSession(): Promise<void> {
	teardownLocalRecording();
	if (!recordingUserId || !recordingFirmId) return; // never started; nothing to restart
	await startRecording(recordingUserId, recordingFirmId);
}

/**
 * After an inactivity close, wait for the user to come back and then start a
 * fresh session. A new session after 30+ idle minutes is the correct outcome —
 * that gap is exactly what separates one session from the next — but without
 * this the tab would simply stop recording for good.
 */
function armRestartOnActivity(): void {
	if (restartArmed) return;
	restartArmed = true;

	const onActivity = () => {
		disarmRestartOnActivity();
		// Activity inside the replay viewer (or while an in-app caller holds the
		// pause) can't start a session — startRecording() would refuse. Stay
		// armed instead of silently consuming the one shot.
		if (recordingPaused || isNoRecordRoute()) {
			armRestartOnActivity();
			return;
		}
		if (!recordingUserId || !recordingFirmId) return;
		startRecording(recordingUserId, recordingFirmId)
			.catch(err => console.warn('Session replay: restart after inactivity failed', err));
	};
	restartListener = onActivity;

	for (const event of ACTIVITY_EVENTS) {
		document.addEventListener(event, onActivity, {passive: true, capture: true, once: true});
	}
}

function disarmRestartOnActivity(): void {
	if (restartListener) {
		for (const event of ACTIVITY_EVENTS) {
			document.removeEventListener(event, restartListener, true);
		}
		restartListener = null;
	}
	restartArmed = false;
}

/** Reset the inactivity timer on user interaction */
function resetInactivityTimer(): void {
	// Same interactions keep the VISIT alive for other tabs, so a second tab
	// opened while the user works here joins this visit rather than starting
	// its own. Throttled to one localStorage write a minute.
	touchVisitActivity();
	if (inactivityTimer) clearTimeout(inactivityTimer);
	inactivityTimer = setTimeout(() => {
		console.info('Session replay: closing session after 30 minutes of inactivity');
		// stopRecording() disarms any pending restart, so arm afterwards.
		stopRecording()
			.catch(() => {})
			.finally(() => armRestartOnActivity());
	}, INACTIVITY_TIMEOUT);
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

function startInactivityWatch(): void {
	for (const event of ACTIVITY_EVENTS) {
		document.addEventListener(event, resetInactivityTimer, {passive: true, capture: true});
	}
	resetInactivityTimer();
}

function stopInactivityWatch(): void {
	if (inactivityTimer) {
		clearTimeout(inactivityTimer);
		inactivityTimer = null;
	}
	for (const event of ACTIVITY_EVENTS) {
		document.removeEventListener(event, resetInactivityTimer, true);
	}
}

/**
 * Flush on page hide / tab close. Uses keepalive so the browser doesn't drop
 * the request when the page is being unloaded.
 */
function handleVisibilityChange(): void {
	if (document.visibilityState === 'hidden') {
		// Tab switch — flush buffered events but keep the session alive.
		// Use keepalive=false: the page is still alive during tab switches,
		// so a regular fetch completes normally. Keepalive is only needed
		// for beforeunload where the page is actually going away.
		flushBuffer(false).catch(err => console.warn('Session replay: visibilitychange flush failed', err));
	}
}

function handleBeforeUnload(): void {
	// Page unload (close tab, navigate away, refresh) — flush but keep session alive.
	// The session will be resumed on next page load, or ended by the server-side
	// inactivity timeout if the user doesn't return.
	//
	// Mark the page as dying first so a 404 on this flush doesn't open a
	// replacement session in a page that's going away. If the unload is
	// canceled, the timer clears the flag; if the page truly unloads, it
	// never runs and the flag is moot.
	unloading = true;
	setTimeout(() => { unloading = false; }, 10_000);
	flushBuffer(true).catch(err => console.warn('Session replay: beforeunload flush failed', err));
}

/**
 * Attach rrweb's recorder to the current page. Shared by startRecording()
 * (initial session start) and resumeRecording() (coming back from a pause).
 */
function startRrwebRecorder(): void {
	stopFn = rrweb.record({
		emit(event) {
			if (eventBuffer.length >= MAX_BUFFER_SIZE) {
				// Drop oldest non-FullSnapshot events to stay under the cap
				const idx = eventBuffer.findIndex((e: any) => e.type !== 2);
				if (idx >= 0) eventBuffer.splice(idx, 1);
				else return; // buffer is all FullSnapshots — stop accepting events
			}
			eventBuffer.push(event);
			if (eventBuffer.length >= EAGER_FLUSH_EVENT_COUNT) {
				scheduleEagerFlush();
			}
		},
		// Emit a new FullSnapshot every 5 minutes so playback can recover
		// even if early chunks are lost.
		checkoutEveryNms: 5 * 60 * 1000,
		// Drop mousemove capture entirely — it's the single largest source of
		// events (one record per animation frame while the cursor is in
		// motion) and contributes the bulk of network volume. Clicks,
		// scrolls, input, and DOM mutations are still recorded, so playback
		// still shows what the user did; the cursor just won't glide between
		// clicks. `mouseInteraction: true` (default) keeps click/hover
		// events. If we later want pointer trails back, switch this to a
		// number (e.g. 100) to throttle instead of disabling.
		sampling: {
			mousemove: false,
		},
		// OWA-693: this MUST stay true, and it does NOT mean "mask everything".
		//
		// rrweb only consults `maskInputFn` for input types present in its
		// internal `maskInputOptions` map, and it derives that map from this
		// flag: `false` yields `{password: true}` — nothing else — so
		// `maskInputFn` is never invoked for a text input and the policy below
		// becomes dead code. (rrweb 2.0.1: `rrweb.js` builds the map; the gate
		// is `maskInputValue()` in `rrweb-snapshot.js`.) That is exactly what
		// happened between OWA-675 and OWA-693: every `data-private` field —
		// SSN, Tax ID, DOB, bank routing/account numbers — recorded verbatim.
		//
		// `true` opts EVERY input type into the masking pipeline, which is the
		// only way our per-element policy gets asked at all. Selectivity then
		// comes from `maskInputFn`, which returns the text unchanged for any
		// field `shouldMaskInput()` does not claim.
		//
		// Regression test: e2e/OWA-675-application-replay-masking.spec.ts
		// gunzips a real replay chunk and asserts the SSN is absent while a
		// control field's value is present.
		maskAllInputs: true,
		// rrweb's maskTextClass accepts only a single class string, which
		// makes it a brittle component-specific hook. Skip it — the
		// Select--private wrapper (the only className-based privacy marker
		// in this codebase) is already paired with data-private on the same
		// element, so maskTextSelector covers it.
		maskTextSelector: '[data-private]',
		// Selectively mask input VALUES in the recording (does NOT affect what
		// the user sees on screen). The policy — and the reasoning behind each
		// rule — lives in sessionReplayMasking.ts, so that forms collecting
		// sensitive data can unit-test it against their own rendered DOM.
		maskInputFn: (text, element) => shouldMaskInput(element) ? '*'.repeat(text.length) : text,
		plugins: [createConsoleRecordPlugin()],
	});

	// Which tab the user is looking at, for the tab-following player (OWA-669).
	// Same function references every time, so re-attaching after a pause does
	// not stack duplicate listeners.
	window.addEventListener('focus', handleWindowFocus);
	window.addEventListener('blur', emitWindowFocusState);
	emitWindowFocusState();
}

/**
 * Start recording a session for the given user and firm.
 * Creates a session on the server, then starts rrweb recording with privacy settings.
 */
export async function startRecording(userId: string, firmId: string): Promise<void> {
	// Remember who we're recording for BEFORE any refusal below, so the
	// restart paths (restartSession, restart-on-activity, resumeRecording)
	// can open a session later even when this call itself is refused — e.g.
	// the tab booted directly onto the replay viewer route.
	recordingUserId = userId;
	recordingFirmId = firmId;

	if (sessionId) return; // Already recording
	if (starting) return; // A start is already in flight — see `starting` above
	if (recordingPaused) return; // Paused by an in-app caller (e.g. replay viewer)
	if (isNoRecordRoute()) return; // Loaded directly onto a no-record route

	disarmRestartOnActivity();

	// Set before the first await so concurrent callers are refused above.
	starting = true;
	const epoch = recordingEpoch;

	try {
		// Join the sitting already in progress in other tabs, or start a new one.
		const visit = acquireVisitId();
		visitId = visit.id;

		// Try to resume a previous session from this browser tab.
		//
		// Skip the resume entirely when the visit went stale: the user has been
		// away longer than the session window, so this is a new sitting. The
		// server's own sweep runs on a deliberately longer window, so without
		// this check a reload after (say) 40 idle minutes would find the old
		// session still marked 'recording' and glue two sittings together —
		// and the resumed row would carry the previous visit's id, putting the
		// session and visit boundaries out of step.
		const previousSessionId = visit.isNew ? null : sessionStorage.getItem(SS_SESSION_ID);
		const previousChunkIndex = sessionStorage.getItem(SS_CHUNK_INDEX);
		let resumed = false;

		if (visit.isNew) clearPersistedSession();

		if (previousSessionId) {
			// Duplicating a tab copies sessionStorage, so the original tab may
			// still be recording into this very session. Both would then upload
			// under the same (session_id, chunk_index) and silently overwrite
			// each other. Claim it first; if the claim fails, leave that session
			// to its owner and open our own below.
			const claimed = await claimSession(previousSessionId);
			if (!claimed) {
				console.info('Session replay: this session belongs to another tab (duplicated tab), starting a new one');
			}
			else {
				// Verify the session is still valid on the server (status = 'recording')
				try {
					const checkResponse = await fetch(`${URI}/replay/sessions/${previousSessionId}`, {
						method: 'GET',
						headers: { 'Authentication': getAuthHeader() },
					});
					if (checkResponse.ok) {
						const session = await checkResponse.json();
						if (session.status === 'recording') {
							sessionId = previousSessionId;
							chunkIndex = previousChunkIndex ? parseInt(previousChunkIndex, 10) : 0;
							resumed = true;
							console.info(`Session replay: resuming session ${sessionId} at chunk ${chunkIndex}`);
						}
					}
				}
				catch {
					// Failed to check — fall through to create new session
				}
			}
			if (!resumed) {
				clearPersistedSession();
			}
		}

		if (!resumed) {
			const response = await fetch(`${URI}/replay/sessions`, {
				method: 'POST',
				headers: {
					'Authentication': getAuthHeader(),
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					initialUrl: window.location.href,
					userAgent: navigator.userAgent,
					viewportWidth: window.innerWidth,
					viewportHeight: window.innerHeight,
					visitId,
				}),
			});

			if (!response.ok) {
				console.warn('Session replay: failed to create session', response.status);
				return;
			}

			const data = await response.json();
			sessionId = data.id as string;
			chunkIndex = 0;
			// Nothing else can own a session id the server just minted, but hold
			// the claim anyway so a later duplicate of THIS tab is detected.
			await claimSession(sessionId);
		}

		if (epoch !== recordingEpoch) {
			// stopRecording() ran during one of the awaits above. Drop what this
			// start acquired instead of recording past the stop. A session the
			// server just created is left for its idle sweep to close.
			sessionId = null;
			chunkIndex = 0;
			await releaseSessionClaimAndWait();
			clearPersistedSession();
			return;
		}

		eventBuffer = [];
		flushing = false;
		lastServerContact = Date.now();
		resetAccumulator();
		persistSessionState();

		startRrwebRecorder();

		// Flush the FullSnapshot immediately rather than waiting 5 seconds.
		// The FullSnapshot is emitted synchronously by rrweb.record(), so
		// eventBuffer already has it. Flushing now prevents the critical
		// first chunk from being lost to a race or page transition.
		setTimeout(() => {
			flushBuffer().catch(err => console.warn('Session replay: initial flush failed', err));
		}, 500);

		flushInterval = setInterval(() => {
			flushIntervalTick().catch(err => console.warn('Session replay: interval tick failed', err));
		}, FLUSH_INTERVAL_MS);

		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('beforeunload', handleBeforeUnload);
		startInactivityWatch();
	}
	catch (err) {
		console.warn('Session replay: startRecording failed', err);
	}
	finally {
		// Released on every path, including the early `return` when the session
		// POST fails: otherwise a single failed start would block recording for
		// the rest of the tab's life. Once a start succeeds, `sessionId` is set
		// and takes over as the guard.
		starting = false;
	}
}

/**
 * Stop recording. Flushes the remaining buffer and marks the session as ended.
 * Snapshots the auth header up front so the final flush succeeds even if
 * localStorage is cleared during logout.
 */
export async function stopRecording(): Promise<void> {
	// Any pending restart-on-activity belongs to a previous inactivity close and
	// must not survive an explicit stop (logout in particular — a click on the
	// login page must never reopen a session). The inactivity path re-arms
	// itself after this returns.
	disarmRestartOnActivity();
	recordingEpoch++;

	if (!sessionId) return;

	// From here to the end of teardown, a 403/404 on our own final flush must
	// not trigger another stop or a restart — see flushBuffer().
	stopping = true;
	try {
		// Capture auth header now — localStorage may be cleared before flush completes
		const authHeader = getAuthHeader();

		if (stopFn) {
			stopFn();
			stopFn = null;
		}

		if (flushInterval) {
			clearInterval(flushInterval);
			flushInterval = null;
		}

		document.removeEventListener('visibilitychange', handleVisibilityChange);
		window.removeEventListener('beforeunload', handleBeforeUnload);
		window.removeEventListener('focus', handleWindowFocus);
		window.removeEventListener('blur', emitWindowFocusState);
		stopInactivityWatch();

		// Wait for any in-flight flush to complete, with a timeout
		const deadline = Date.now() + FLUSH_WAIT_TIMEOUT;
		while (flushing && Date.now() < deadline) {
			await new Promise(resolve => setTimeout(resolve, 50));
		}
		if (flushing) {
			console.warn('Session replay: timed out waiting for in-flight flush');
		}

		await flushBuffer(false, authHeader);

		const endedSessionId = sessionId;
		sessionId = null;
		releaseSessionClaim?.();
		clearPersistedSession();

		// Pause is scoped to an active session. If the user logs out while on a
		// no-record route (/session-replay), the Tab's cleanup effect may
		// not fire before the next startRecording() — without this reset, the
		// flag would stick and silently block future recording in this tab.
		recordingPaused = false;

		const summary = buildAccumulatedSummary();

		try {
			await fetch(`${URI}/replay/sessions/${endedSessionId}`, {
				method: 'PATCH',
				headers: {
					'Authentication': authHeader,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({summary}),
			});
		}
		catch (err) {
			console.warn('Session replay: failed to mark session ended', err);
		}
	}
	finally {
		stopping = false;
	}
}

/**
 * Pause rrweb emission without ending the server-side session. Keeps
 * sessionId, chunkIndex, and the flush interval alive so resumeRecording()
 * continues uploading into the same session. Use this when entering a route
 * whose DOM we don't want to record (e.g. the replay viewer itself).
 */
export function pauseRecording(): void {
	if (recordingPaused) return;
	recordingPaused = true;
	if (!sessionId) return; // Nothing to tear down yet; flag alone is enough
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	flushBuffer(false).catch(err => console.warn('Session replay: pause flush failed', err));
}

/**
 * Resume rrweb emission into the currently-active session. If the session is
 * still open, rrweb re-attaches and emits a fresh FullSnapshot that playback
 * uses to rebuild the DOM after the pause gap. If recording never started,
 * this just clears the pause flag so future startRecording() calls proceed.
 */
export function resumeRecording(): void {
	const wasPaused = recordingPaused;
	recordingPaused = false;
	if (sessionId && wasPaused && !stopFn) {
		startRrwebRecorder();
		return;
	}
	// The session may have died while paused (server reaped it during a long
	// stay in the replay viewer), or recording was refused outright because the
	// tab loaded directly onto a no-record route. Now that emission is allowed
	// again, start fresh rather than leaving the tab unrecorded until the next
	// full reload. startRecording() safely refuses if this is still the wrong
	// place or a session somehow exists.
	if (!sessionId && wasPaused && recordingUserId && recordingFirmId) {
		// No preemptive disarm: startRecording() disarms itself once it actually
		// proceeds, and if it refuses (still on a no-record route) any armed
		// restart must survive to catch the user's next interaction.
		startRecording(recordingUserId, recordingFirmId)
			.catch(err => console.warn('Session replay: restart after pause failed', err));
	}
}

/**
 * Test-only hooks. Do not import from production code. Exported so unit tests
 * can seed and inspect module-level singleton state without invoking the
 * full rrweb/fetch pipeline.
 */
export const __test__ = {
	isPaused: () => recordingPaused,
	getSessionId: () => sessionId,
	getChunkIndex: () => chunkIndex,
	getBufferLength: () => eventBuffer.length,
	pushEvent: (e: object) => { eventBuffer.push(e); },
	flushBuffer: (keepalive = false) => flushBuffer(keepalive),
	setSessionId: (id: string | null) => { sessionId = id; },
	setPaused: (v: boolean) => { recordingPaused = v; },
	isRestartArmed: () => restartArmed,
	getLastServerContact: () => lastServerContact,
	setLastServerContact: (t: number) => { lastServerContact = t; },
	setCredentials: (userId: string | null, firmId: string | null) => {
		recordingUserId = userId;
		recordingFirmId = firmId;
	},
	sendHeartbeat: () => sendHeartbeat(),
	flushIntervalTick: () => flushIntervalTick(),
	armRestartOnActivity: () => armRestartOnActivity(),
	getInactivityTimeout: () => INACTIVITY_TIMEOUT,
	getHeartbeatIdleMs: () => HEARTBEAT_IDLE_MS,
	getVisitIdleTimeout: () => VISIT_IDLE_TIMEOUT,
	getVisitId: () => visitId,
	acquireVisitId: () => acquireVisitId().id,
	acquireVisit: () => acquireVisitId(),
	claimSession: (id: string) => claimSession(id),
	releaseSessionClaim: () => releaseSessionClaimAndWait(),
	reset: () => {
		disarmRestartOnActivity();
		releaseSessionClaim?.();
		visitId = null;
		lastVisitActivityWrite = 0;
		// Actually cancel the timers — tests that call startRecording() leave a
		// live interval behind, and it would keep firing into later tests.
		if (flushInterval) clearInterval(flushInterval);
		if (inactivityTimer) clearTimeout(inactivityTimer);
		sessionId = null;
		recordingPaused = false;
		stopFn = null;
		flushInterval = null;
		inactivityTimer = null;
		eventBuffer = [];
		chunkIndex = 0;
		flushing = false;
		eagerFlushQueued = false;
		recordingUserId = null;
		recordingFirmId = null;
		lastServerContact = 0;
		stopping = false;
		starting = false;
		unloading = false;
	},
};
